/**
 * Export: real files, out of the seeded rows, in the browser.
 *
 * A visible export control is the cheapest honest signal a cloud can send,
 * and an export control that opens a toast and produces nothing is the
 * opposite of that signal. So these produce actual files from the actual
 * rows: the CSV opens in a spreadsheet, and the statements re-create the
 * table somewhere else.
 *
 * Nothing here knows about React, and nothing here writes copy. The two
 * quoting rules are the whole substance of the file, and both are the boring
 * correct ones: a CSV field is quoted when it contains a separator, a quote
 * or a newline, and a quote inside it is doubled; a string literal in a
 * statement is single quoted and a quote inside it is doubled.
 */

import type { DbCell, DbColumn, DbRow } from './types';

function csvField(value: DbCell): string {
  if (value === null) return '';
  const text = typeof value === 'boolean' ? String(value) : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function toCsv(columns: string[], rows: DbRow[]): string {
  const lines = [columns.join(',')];
  for (const row of rows) {
    lines.push(columns.map((name) => csvField(row.cells[name])).join(','));
  }
  return `${lines.join('\n')}\n`;
}

function sqlLiteral(value: DbCell): string {
  if (value === null) return 'null';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return `'${value.replace(/'/g, "''")}'`;
}

export function toSqlInserts(table: string, columns: string[], rows: DbRow[]): string {
  const names = columns.join(', ');
  return rows
    .map(
      (row) =>
        `insert into ${table} (${names}) values (${columns
          .map((name) => sqlLiteral(row.cells[name]))
          .join(', ')});`,
    )
    .join('\n');
}

/** The column type as a portable declaration, for a result nobody owns yet. */
function declare(column: DbColumn): string {
  switch (column.type) {
    case 'number':
      return 'numeric';
    case 'boolean':
      return 'boolean';
    case 'timestamp':
      return 'timestamp';
    case 'json':
      return 'jsonb';
    case 'uuid':
      return 'uuid';
    default:
      return 'text';
  }
}

export function toSqlCreate(table: string, columns: DbColumn[]): string {
  const body = columns
    .map((column) => {
      const nullable = column.nullable ? '' : ' not null';
      const key = column.primaryKey ? ' primary key' : '';
      return `  ${column.name} ${declare(column)}${nullable}${key}`;
    })
    .join(',\n');
  return `create table ${table} (\n${body}\n);`;
}

export interface ExportTable {
  name: string;
  columns: DbColumn[];
  rows: DbRow[];
}

/** One table: its shape, then its rows. */
export function toSqlTable(table: ExportTable): string {
  const names = table.columns.map((column) => column.name);
  const inserts = toSqlInserts(table.name, names, table.rows);
  return `${toSqlCreate(table.name, table.columns)}\n${inserts}\n`;
}

/** Every table in one file, in the order the list showed them. */
export function toSqlDump(tables: ExportTable[]): string {
  return tables.map((table) => toSqlTable(table)).join('\n');
}

/**
 * Every table in one CSV, each section headed by its own name.
 *
 * One file per table is the convention, and a browser cannot hand over a
 * folder, so the choice is between several downloads a person did not ask for
 * and one file that says what is in it. This is the second, and the menu item
 * says so in words before anybody clicks it.
 */
export function toCsvBook(tables: ExportTable[]): string {
  return tables
    .map((table) => {
      const names = table.columns.map((column) => column.name);
      return `# ${table.name}\n${toCsv(names, table.rows)}`;
    })
    .join('\n');
}

/** Hands the file to the browser. The object URL is released straight after. */
export function downloadText(filename: string, text: string, mime: string): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
